import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import {
  ISiteVisit,
  ISiteVisitPosition,
  ISiteVisitCategory,
  ISiteVisitQuestion,
  QuestionResultMap,
  SiteVisitQuestionType,
  ICategoryProgress,
  IQuestionProgress,
  ISiteVisitProgress,
} from '../types';
import { useSiteVisitState } from './hooks/useSiteVisitState';
import { useSiteVisitNavigation } from './hooks/useSiteVisitNavigation';
import { useSiteVisitData } from './hooks/useSiteVisitData';
import { sanitizeSiteVisitData } from '../utils';

interface ISiteVisitContextState {
  siteVisit: ISiteVisit | null;
  position: ISiteVisitPosition;
  isLoading: boolean;
  error: string | null;
}

interface ISiteVisitContext extends ISiteVisitContextState {
  nextQuestion: () => Promise<boolean>;
  previousQuestion: () => boolean;
  goToQuestion: (categoryIndex: number, questionIndex: number) => boolean;
  goToCategory: (categoryIndex: number) => boolean;
  getCurrentCategory: () => ISiteVisitCategory | null;
  getCurrentQuestion: () => ISiteVisitQuestion | null;
  getCategoryQuestions: (categoryIndex: number) => IQuestionProgress[];
  updateSiteVisit: (siteVisit: ISiteVisit) => void;
  initializeSiteVisit: (rawSiteVisit: Record<string, unknown>) => void;
  updateQuestionResult: <T extends SiteVisitQuestionType>(
    result: QuestionResultMap[T] | undefined,
    categoryIndex?: number,
    questionIndex?: number
  ) => void;
  isFirstQuestion: () => boolean;
  isLastQuestion: () => boolean;
  isComplete: () => boolean;
  getProgress: () => ISiteVisitProgress;
  getCategoriesProgress: () => ICategoryProgress[];
  saveSiteVisitAndRedirect: (redirect?: boolean) => Promise<void>;
}

const SiteVisitContext = createContext<ISiteVisitContext | undefined>(undefined);

interface ISiteVisitProviderProps {
  children: ReactNode;
  initialSiteVisit?: ISiteVisit | null;
  onProgress?: (progress: ISiteVisitProgress) => void;
}

export const SiteVisitProvider: React.FC<ISiteVisitProviderProps> = ({
  children,
  initialSiteVisit = null,
  onProgress,
}) => {
  const {
    siteVisit,
    setSiteVisit,
    position,
    setPosition,
    isLoading,
    setIsLoading,
    error,
    setError,
    initializeSiteVisit,
    updateSiteVisit,
  } = useSiteVisitState(sanitizeSiteVisitData(initialSiteVisit));

  const {
    saveSiteVisitToServer,
    getCurrentCategory,
    getCurrentQuestion,
    updateQuestionResult,
    isComplete,
    getProgress,
    getCategoriesProgress,
    getCategoryQuestions,
    saveSiteVisitAndRedirect,
  } = useSiteVisitData({
    siteVisit,
    setSiteVisit,
    position,
    setIsLoading,
    setError,
    onProgress,
  });

  const {
    isFirstQuestion,
    isLastQuestion,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    goToCategory,
  } = useSiteVisitNavigation({
    siteVisit,
    position,
    setPosition,
    saveSiteVisitToServer,
  });

  const contextValue = useMemo(
    () => ({
      siteVisit,
      position,
      isLoading,
      error,
      nextQuestion,
      previousQuestion,
      goToQuestion,
      goToCategory,
      getCurrentCategory,
      getCurrentQuestion,
      getCategoryQuestions,
      updateSiteVisit,
      initializeSiteVisit,
      updateQuestionResult,
      isFirstQuestion,
      isLastQuestion,
      isComplete,
      getProgress,
      getCategoriesProgress,
      saveSiteVisitAndRedirect,
    }),
    [
      siteVisit,
      position,
      isLoading,
      error,
      nextQuestion,
      previousQuestion,
      goToQuestion,
      goToCategory,
      getCurrentCategory,
      getCurrentQuestion,
      getCategoryQuestions,
      updateSiteVisit,
      initializeSiteVisit,
      updateQuestionResult,
      isFirstQuestion,
      isLastQuestion,
      isComplete,
      getProgress,
      getCategoriesProgress,
      saveSiteVisitAndRedirect,
    ]
  );

  return <SiteVisitContext.Provider value={contextValue}>{children}</SiteVisitContext.Provider>;
};

export const useSiteVisit = (): ISiteVisitContext => {
  const context = useContext(SiteVisitContext);
  if (context === undefined) {
    throw new Error('useSiteVisit must be used within a SiteVisitProvider');
  }
  return context;
};
